import { LitElement, html, nothing, type TemplateResult, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import styles from './styles.js';
import type { MenuItem, MenuItemCommandEvent, MenuItemSelectEventDetail } from '../types.js';
import { emit } from '../utils/dom.utils.js';

/* ─────────────────────────────────────────────────────────────────────────────
   MegaMenu data types  (exported so host apps can import them)
───────────────────────────────────────────────────────────────────────────── */

/** A single column of items rendered inside a mega panel. */
export interface MegaMenuColumn {
  /** Optional section heading above the item list. */
  label?: string;
  /** Items belonging to this column. Supports all standard MenuItem fields. */
  items: MenuItem[];
}

/** A top-level mega-menu entry. */
export interface MegaMenuItem {
  /** Display text. */
  label?: string;
  /** Icon name rendered before the label (resolved via the configured library). */
  icon?: string;
  /** Name of the icon library registered via `registerIconLibrary`. Defaults to `'default'`. */
  iconLibrary?: string;
  /** Extra CSS class(es) for the icon wrapper. */
  iconClass?: string;
  /** Invoked when a leaf item (no columns) is activated. */
  command?: (e: MenuItemCommandEvent) => void;
  /** Renders the top-level item as an <a> link when set (leaf items only). */
  url?: string;
  /** Anchor target; used together with url. */
  target?: string;
  /**
   * Columns displayed in the mega panel.
   * When omitted the item is treated as a direct-action leaf.
   */
  items?: MegaMenuColumn[];
  /** Disabled — visible but not interactive. */
  disabled?: boolean;
  /** When false the item is excluded from rendering entirely. */
  visible?: boolean;
  /** Horizontal rule in the top-level bar. */
  separator?: boolean;
  /** Short badge text at the trailing edge. */
  badge?: string;
  /** Extra CSS class(es) for the badge span. */
  badgeClass?: string;
  /** Native browser tooltip. */
  title?: string;
  /** Unique id used for aria-controls and React keying. */
  id?: string;
  /** Arbitrary data attached to this item — passed through events. */
  data?: unknown;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

/**
 * `uwc-megamenu`
 *
 * A full-featured navigation bar (or sidebar) with multi-column mega panels.
 *
 * **Horizontal** (default): horizontal bar — clicking / hovering a top-level
 * item that has `items` opens a full-bar-width panel below the bar containing
 * the configured columns.
 *
 * **Vertical**: sidebar list — clicking expands columns inline (accordion).
 *
 * Both orientations adapt all panel text and background automatically when you
 * change `--uwc-megamenu-panel-bg` and `--uwc-megamenu-panel-color`.
 *
 * @attr  items        — MegaMenuItem[] navigation model.
 * @attr  orientation  — `'horizontal'` (default) | `'vertical'`.
 * @attr  aria-label   — Accessible landmark label. Default: `'Navigation'`.
 *
 * @fires uwc-item-select — `{ item: MenuItem, originalEvent: Event }`
 *
 * @slot start — Leading area (logo / brand); horizontal mode.
 * @slot end   — Trailing area (search, avatar); horizontal mode.
 *
 * @cssprop --uwc-megamenu-bg
 * @cssprop --uwc-megamenu-color
 * @cssprop --uwc-megamenu-border
 * @cssprop --uwc-megamenu-radius
 * @cssprop --uwc-megamenu-shadow
 * @cssprop --uwc-megamenu-padding
 * @cssprop --uwc-megamenu-height
 * @cssprop --uwc-megamenu-item-padding
 * @cssprop --uwc-megamenu-item-radius
 * @cssprop --uwc-megamenu-item-hover-bg
 * @cssprop --uwc-megamenu-item-active-bg
 * @cssprop --uwc-megamenu-item-font-size
 * @cssprop --uwc-megamenu-item-font-weight
 * @cssprop --uwc-megamenu-panel-bg
 * @cssprop --uwc-megamenu-panel-color
 * @cssprop --uwc-megamenu-panel-border
 * @cssprop --uwc-megamenu-panel-radius
 * @cssprop --uwc-megamenu-panel-shadow
 * @cssprop --uwc-megamenu-panel-padding
 * @cssprop --uwc-megamenu-panel-max-height
 * @cssprop --uwc-megamenu-col-gap
 * @cssprop --uwc-megamenu-col-header-color
 * @cssprop --uwc-megamenu-col-header-font-size
 * @cssprop --uwc-megamenu-col-header-font-weight
 * @cssprop --uwc-megamenu-col-item-padding
 * @cssprop --uwc-megamenu-col-item-radius
 * @cssprop --uwc-megamenu-col-item-hover-bg
 * @cssprop --uwc-megamenu-col-item-font-size
 * @cssprop --uwc-megamenu-col-item-danger-color
 * @cssprop --uwc-megamenu-separator-color
 * @cssprop --uwc-megamenu-badge-bg
 * @cssprop --uwc-megamenu-badge-color
 * @cssprop --uwc-megamenu-z
 */

export class UwcMegaMenu extends LitElement {
  static override styles = styles;

  /* ── Public properties ───────────────────────────────────────────── */

  @property({ type: Array })
  items: MegaMenuItem[] = [];

  @property({ reflect: true })
  orientation: 'horizontal' | 'vertical' = 'horizontal';

  @property({ attribute: 'aria-label' })
  ariaLabel = 'Navigation';

  /* ── Internal state ──────────────────────────────────────────────── */

  /** Index (within visible items) of the open mega panel. null = closed. */
  @state() private _activeIndex: number | null = null;

  /** Whether the mobile panel is visible (horizontal mode only). */
  @state() private _mobileOpen = false;

  /** Expanded set for the mobile accordion, keyed as "m-{idx}". */
  @state() private _mobileExpanded: Set<string> = new Set();

  /** Index of the persistently selected top-level item (set on leaf click). */
  @state() private _selectedTopIndex: number | null = null;

  /** Key of the persistently selected column item: "{panelIdx}-{colIdx}-{itemIdx}". */
  @state() private _selectedColKey: string | null = null;

  /* ── Hover timer handles ─────────────────────────────────────────── */

  private _hoverOpenTimer:  ReturnType<typeof setTimeout> | null = null;
  private _hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /* ── Outside-click handler ───────────────────────────────────────── */

  private readonly _onDocClick = (e: MouseEvent): void => {
    if (!e.composedPath().includes(this)) this._closeAll();
  };

  /* ── Scroll / resize reposition ──────────────────────────────────── */

  private readonly _onScrollOrResize = (): void => {
    if (this._activeIndex !== null || this._mobileOpen) this._syncPopovers();
  };

  /* ── Lifecycle ───────────────────────────────────────────────────── */

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this._onDocClick);
    window.addEventListener('scroll',  this._onScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize',  this._onScrollOrResize, { passive: true });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocClick);
    window.removeEventListener('scroll',  this._onScrollOrResize, { capture: true });
    window.removeEventListener('resize',  this._onScrollOrResize);
    this._clearHoverTimers();
  }

  /* ── Helpers ─────────────────────────────────────────────────────── */

  private _clearHoverTimers(): void {
    if (this._hoverOpenTimer  !== null) { clearTimeout(this._hoverOpenTimer);  this._hoverOpenTimer  = null; }
    if (this._hoverCloseTimer !== null) { clearTimeout(this._hoverCloseTimer); this._hoverCloseTimer = null; }
  }

  private _closeAll(): void {
    this._activeIndex = null;
    this._mobileOpen  = false;
  }

  private _visibleItems(): MegaMenuItem[] {
    return this.items.filter(it => it.visible !== false);
  }

  private _focusableTopItems(): { item: MegaMenuItem; idx: number }[] {
    return this._visibleItems()
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.separator && !item.disabled);
  }

  private _focusTopBtn(idx: number): void {
    requestAnimationFrame(() => {
      this.shadowRoot?.querySelector<HTMLElement>(`[data-top-index="${idx}"]`)?.focus();
    });
  }

  /* ── Popover sync (horizontal) ───────────────────────────────────── */

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('_activeIndex') || changed.has('_mobileOpen')) {
      this._syncPopovers();
    }
  }

  private _syncPopovers(): void {
    if (this.orientation !== 'horizontal') return;

    const bar = this.shadowRoot?.querySelector<HTMLElement>('.uwc-mm');
    const panels: NodeListOf<HTMLElement> | HTMLElement[] = this.shadowRoot?.querySelectorAll<HTMLElement>('[data-mega-panel]') ?? [];

    for (const panel of panels) {
      const panelIdx = Number(panel.dataset.megaPanel);

      if (panelIdx === this._activeIndex && bar) {
        const r = bar.getBoundingClientRect();
        panel.style.left      = `${r.left}px`;
        panel.style.top       = `${r.bottom + 4}px`;
        panel.style.width     = `${r.width}px`;
        panel.style.maxHeight = `${Math.max(200, window.innerHeight - r.bottom - 24)}px`;
        try { panel.showPopover(); } catch { /* already open */ }
      } else {
        try { panel.hidePopover(); } catch { /* already closed */ }
      }
    }

    // Mobile panel
    const mobilePanel = this.shadowRoot?.querySelector<HTMLElement>('#uwc-mm-mobile');
    if (!mobilePanel) return;
    if (this._mobileOpen && bar) {
      const r = bar.getBoundingClientRect();
      mobilePanel.style.left      = `${r.left}px`;
      mobilePanel.style.top       = `${r.bottom + 4}px`;
      mobilePanel.style.width     = `${r.width}px`;
      mobilePanel.style.maxHeight = `${Math.max(200, window.innerHeight - r.bottom - 24)}px`;
      try { mobilePanel.showPopover(); } catch { /* already open */ }
    } else {
      try { mobilePanel.hidePopover(); } catch { /* already closed */ }
    }
  }

  /* ── Hover logic (desktop, horizontal) ──────────────────────────── */

  private _onItemMouseEnter(idx: number): void {
    if (this._hoverCloseTimer !== null) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    }
    // Switch to hovered panel only while menu is already open
    if (this._activeIndex === null) return;
    this._hoverOpenTimer = setTimeout(() => { this._activeIndex = idx; }, 80);
  }

  private _onItemMouseLeave(): void {
    if (this._hoverOpenTimer !== null) {
      clearTimeout(this._hoverOpenTimer);
      this._hoverOpenTimer = null;
    }
    this._hoverCloseTimer = setTimeout(() => { this._activeIndex = null; }, 150);
  }

  private _onPanelMouseEnter(): void {
    if (this._hoverCloseTimer !== null) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    }
  }

  private _onPanelMouseLeave(): void {
    this._hoverCloseTimer = setTimeout(() => { this._activeIndex = null; }, 150);
  }

  /* ── Item execution ──────────────────────────────────────────────── */

  private _executeItem(item: MenuItem, e: Event): void {
    if (item.disabled) return;
    item.command?.({ originalEvent: e, item });
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', { item, originalEvent: e });
    this._closeAll();
  }

  /* ── Keyboard: top-level bar ─────────────────────────────────────── */

  private _handleTopKeydown(e: KeyboardEvent, item: MegaMenuItem, idx: number): void {
    const focusable = this._focusableTopItems();
    const pos = focusable.findIndex(f => f.idx === idx);

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = focusable[(pos + 1) % focusable.length];
        if (next) { this._activeIndex = null; this._focusTopBtn(next.idx); }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = focusable[(pos - 1 + focusable.length) % focusable.length];
        if (prev) { this._activeIndex = null; this._focusTopBtn(prev.idx); }
        break;
      }
      case 'ArrowDown':
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (item.items?.length) {
          this._activeIndex = idx;
          requestAnimationFrame(() => {
            const first = this.shadowRoot?.querySelector<HTMLElement>(
              `[data-mega-panel="${idx}"] .uwc-mm__col-item:not([disabled])`
            );
            first?.focus();
          });
        } else {
          this._selectedTopIndex = idx;
          this._selectedColKey = null;
          this._executeItem(item as unknown as MenuItem, e);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        this._activeIndex = null;
        break;
      }
      case 'Tab': {
        this._closeAll();
        break;
      }
    }
  }

  /* ── Keyboard: mega panel column items ───────────────────────────── */

  private _handleColItemKeydown(
    e:       KeyboardEvent,
    item:    MenuItem,
    colIdx:  number,
    itemIdx: number,
    panelIdx: number,
  ): void {
    switch (e.key) {
      case 'Escape': {
        e.preventDefault();
        this._activeIndex = null;
        this._focusTopBtn(panelIdx);
        break;
      }
      case 'Tab': {
        this._closeAll();
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        this._focusNextInCol(panelIdx, colIdx, itemIdx, 1);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        this._focusNextInCol(panelIdx, colIdx, itemIdx, -1);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        this._focusFirstInCol(panelIdx, colIdx + 1);
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        this._focusFirstInCol(panelIdx, colIdx - 1);
        break;
      }
      case 'Home': {
        e.preventDefault();
        this._focusNthInCol(panelIdx, colIdx, 0);
        break;
      }
      case 'End': {
        e.preventDefault();
        const cols = this.items[panelIdx]?.items ?? [];
        const col  = cols[colIdx];
        const last = (col?.items ?? []).filter(it => !it.separator && !it.disabled && it.visible !== false).length - 1;
        this._focusNthInCol(panelIdx, colIdx, last);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        this._selectedTopIndex = panelIdx;
        this._selectedColKey = `${panelIdx}-${colIdx}-${itemIdx}`;
        this._executeItem(item, e);
        break;
      }
    }
  }

  private _focusNextInCol(panelIdx: number, colIdx: number, itemIdx: number, dir: 1 | -1): void {
    const cols = this.items[panelIdx]?.items ?? [];
    const col  = cols[colIdx];
    if (!col) return;
    const focusable = col.items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => !it.separator && !it.disabled && it.visible !== false);
    const pos     = focusable.findIndex(({ i }) => i === itemIdx);
    const nextPos = (pos + dir + focusable.length) % focusable.length;
    const target  = focusable[nextPos];
    if (target) {
      requestAnimationFrame(() => {
        this.shadowRoot?.querySelector<HTMLElement>(
          `[data-mega-panel="${panelIdx}"] [data-col="${colIdx}"][data-item="${target.i}"]`
        )?.focus();
      });
    }
  }

  private _focusFirstInCol(panelIdx: number, colIdx: number): void {
    const cols = this.items[panelIdx]?.items ?? [];
    const col  = cols[colIdx];
    if (!col) return;
    const first = col.items.findIndex(it => !it.separator && !it.disabled && it.visible !== false);
    if (first !== -1) {
      requestAnimationFrame(() => {
        this.shadowRoot?.querySelector<HTMLElement>(
          `[data-mega-panel="${panelIdx}"] [data-col="${colIdx}"][data-item="${first}"]`
        )?.focus();
      });
    }
  }

  private _focusNthInCol(panelIdx: number, colIdx: number, nth: number): void {
    const cols = this.items[panelIdx]?.items ?? [];
    const col  = cols[colIdx];
    if (!col) return;
    const focusable = col.items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => !it.separator && !it.disabled && it.visible !== false);
    const target = focusable[Math.max(0, Math.min(nth, focusable.length - 1))];
    if (target) {
      requestAnimationFrame(() => {
        this.shadowRoot?.querySelector<HTMLElement>(
          `[data-mega-panel="${panelIdx}"] [data-col="${colIdx}"][data-item="${target.i}"]`
        )?.focus();
      });
    }
  }

  /* ── Render helpers ──────────────────────────────────────────────── */

  /** Top-level item button / link content (icon + label + badge + chevron). */
  private _renderTopItemContent(item: MegaMenuItem, hasMega: boolean, isActive: boolean): TemplateResult {
    return html`
      ${item.icon
        ? html`<span class="uwc-mm__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.15em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-mm__item-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-mm__item-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}
      ${hasMega
        ? html`<uwc-icon
            name=${isActive ? 'chevron-up' : 'chevron-down'}
            size="0.85em"
            class="uwc-mm__chevron"
            aria-hidden="true"
          ></uwc-icon>`
        : nothing}`;
  }

  /** Horizontal top-level item (bar button + optional mega panel). */
  private _renderTopItemH(item: MegaMenuItem, idx: number): TemplateResult {
    if (item.visible === false) return html``;
    if (item.separator) {
      return html`
        <li role="none" class="uwc-mm__item">
          <div class="uwc-mm__h-sep" role="separator" aria-hidden="true"></div>
        </li>`;
    }

    const hasMega  = Boolean(item.items?.length);
    const isActive = this._activeIndex === idx;
    const ctrlId   = `uwc-mm-panel-${idx}`;

    const btn = item.url && !hasMega && !item.disabled
      ? html`
          <a
            id="uwc-mm-top-${idx}"
            href=${item.url}
            target=${item.target ?? '_self'}
            role="menuitem"
            class="uwc-mm__item-btn"
            data-top-index=${idx}
            title=${item.title ?? ''}
            @click=${(e: MouseEvent) => { e.stopPropagation(); this._selectedTopIndex = idx; this._selectedColKey = null; this._executeItem(item as unknown as MenuItem, e); }}
            @keydown=${(e: KeyboardEvent) => this._handleTopKeydown(e, item, idx)}
          >${this._renderTopItemContent(item, hasMega, isActive)}</a>`
      : html`
          <button
            id="uwc-mm-top-${idx}"
            type="button"
            role="menuitem"
            class="uwc-mm__item-btn"
            data-top-index=${idx}
            ?disabled=${item.disabled}
            aria-haspopup=${hasMega ? 'menu' : 'false'}
            aria-expanded=${hasMega ? String(isActive) : 'false'}
            aria-controls=${hasMega ? ctrlId : ''}
            title=${item.title ?? ''}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              if (item.disabled) return;
              this._clearHoverTimers();
              if (hasMega) {
                this._activeIndex = isActive ? null : idx;
              } else {
                this._selectedTopIndex = idx;
                this._selectedColKey = null;
                this._executeItem(item as unknown as MenuItem, e);
              }
            }}
            @keydown=${(e: KeyboardEvent) => this._handleTopKeydown(e, item, idx)}
          >${this._renderTopItemContent(item, hasMega, isActive)}</button>`;

    return html`
      <li
        role="none"
        class=${classMap({ 'uwc-mm__item': true, 'uwc-mm__item--active': isActive, 'uwc-mm__item--selected': this._selectedTopIndex === idx })}
        @mouseenter=${() => hasMega ? this._onItemMouseEnter(idx) : undefined}
        @mouseleave=${() => hasMega ? this._onItemMouseLeave()    : undefined}
      >
        ${btn}
        ${hasMega ? html`
          <div
            id=${ctrlId}
            class="uwc-mm__panel"
            role="region"
            popover="manual"
            aria-label=${item.label ?? ''}
            data-mega-panel=${idx}
            @mouseenter=${() => this._onPanelMouseEnter()}
            @mouseleave=${() => this._onPanelMouseLeave()}
          >
            <div class="uwc-mm__columns">
              ${(item.items ?? []).map((col, ci) => this._renderColumn(col, ci, idx))}
            </div>
          </div>` : nothing}
      </li>`;
  }

  /** One column (header + item list) inside the mega panel. */
  private _renderColumn(col: MegaMenuColumn, colIdx: number, panelIdx: number): TemplateResult {
    const headerId = col.label ? `uwc-mm-ch-${panelIdx}-${colIdx}` : '';
    return html`
      <div
        class="uwc-mm__col"
        role="group"
        aria-labelledby=${headerId}
      >
        ${col.label
          ? html`<p id=${headerId} class="uwc-mm__col-header">${col.label}</p>`
          : nothing}
        <ul class="uwc-mm__col-list" role="menu" aria-label=${col.label ?? ''}>
          ${col.items
            .filter(it => it.visible !== false)
            .map((item, itemIdx) => this._renderColItem(item, colIdx, itemIdx, panelIdx))}
        </ul>
      </div>`;
  }

  /** A single item inside a mega panel column. */
  private _renderColItem(item: MenuItem, colIdx: number, itemIdx: number, panelIdx: number): TemplateResult {
    if (item.separator) {
      return html`<li role="separator" class="uwc-mm__separator" aria-hidden="true"></li>`;
    }

    const cls = classMap({
      'uwc-mm__col-item':           true,
      'uwc-mm__col-item--danger':   !!item.danger,
      'uwc-mm__col-item--disabled': !!item.disabled,
      'uwc-mm__col-item--selected': this._selectedColKey === `${panelIdx}-${colIdx}-${itemIdx}`,
    });

    const inner = html`
      ${item.icon
        ? html`<span class="uwc-mm__col-item-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-mm__col-item-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-mm__col-item-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}`;

    const handlers = {
      click:   (e: MouseEvent) => { e.stopPropagation(); this._selectedTopIndex = panelIdx; this._selectedColKey = `${panelIdx}-${colIdx}-${itemIdx}`; this._executeItem(item, e); },
      keydown: (e: KeyboardEvent) => this._handleColItemKeydown(e, item, colIdx, itemIdx, panelIdx),
    };

    return html`
      <li role="none" class="uwc-mm__col-li">
        ${item.url && !item.disabled
          ? html`
            <a
              href=${item.url}
              target=${item.target ?? '_self'}
              role="menuitem"
              class=${cls}
              data-col=${colIdx}
              data-item=${itemIdx}
              tabindex="-1"
              title=${item.title ?? ''}
              @click=${handlers.click}
              @keydown=${handlers.keydown}
            >${inner}</a>`
          : html`
            <button
              type="button"
              role="menuitem"
              class=${cls}
              data-col=${colIdx}
              data-item=${itemIdx}
              tabindex="-1"
              ?disabled=${item.disabled}
              title=${item.title ?? ''}
              @click=${handlers.click}
              @keydown=${handlers.keydown}
            >${inner}</button>`}
      </li>`;
  }

  /* ── Vertical orientation renders ───────────────────────────────── */

  private _renderTopItemV(item: MegaMenuItem, idx: number): TemplateResult {
    if (item.visible === false) return html``;
    if (item.separator) {
      return html`<li role="separator" class="uwc-mm__separator" aria-hidden="true"></li>`;
    }

    const hasMega  = Boolean(item.items?.length);
    const isActive = this._activeIndex === idx;

    return html`
      <li
        role="none"
        class=${classMap({ 'uwc-mm__v-item': true, 'uwc-mm__v-item--active': isActive, 'uwc-mm__v-item--selected': this._selectedTopIndex === idx })}
      >
        <button
          type="button"
          role="treeitem"
          class="uwc-mm__v-item-btn"
          data-top-index=${idx}
          ?disabled=${item.disabled}
          aria-expanded=${hasMega ? String(isActive) : 'false'}
          title=${item.title ?? ''}
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            if (item.disabled) return;
            this._activeIndex = hasMega
              ? (isActive ? null : idx)
              : null;
            if (!hasMega) {
              this._selectedTopIndex = idx;
              this._selectedColKey = null;
              this._executeItem(item as unknown as MenuItem, e);
            }
          }}
        >
          ${item.icon
            ? html`<span class="uwc-mm__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
                <uwc-icon name=${item.icon} size="1.15em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
              </span>`
            : nothing}
          <span class="uwc-mm__item-label">${item.label ?? ''}</span>
          ${item.badge
            ? html`<span class="uwc-mm__item-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
            : nothing}
          ${hasMega
            ? html`<uwc-icon
                name=${isActive ? 'chevron-up' : 'chevron-down'}
                size="0.85em"
                class="uwc-mm__chevron"
                aria-hidden="true"
              ></uwc-icon>`
            : nothing}
        </button>

        ${hasMega && isActive
          ? html`
            <div class="uwc-mm__v-panel">
              <div class="uwc-mm__columns uwc-mm__columns--v">
                ${(item.items ?? []).map((col, ci) => this._renderColumn(col, ci, idx))}
              </div>
            </div>`
          : nothing}
      </li>`;
  }

  /* ── Mobile accordion renders ────────────────────────────────────── */

  private _renderMobileItem(item: MegaMenuItem, idx: number): TemplateResult {
    if (item.visible === false) return html``;
    if (item.separator) {
      return html`<li role="separator" class="uwc-mm__separator" aria-hidden="true"></li>`;
    }

    const hasMega = Boolean(item.items?.length);
    const mKey    = `m-${idx}`;
    const isOpen  = this._mobileExpanded.has(mKey);

    return html`
      <li role="none" class=${classMap({ 'uwc-mm__mob-item': true, 'uwc-mm__mob-item--open': isOpen })}>
        <button
          type="button"
          class="uwc-mm__mob-btn"
          ?disabled=${item.disabled}
          aria-expanded=${hasMega ? String(isOpen) : 'false'}
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            if (item.disabled) return;
            if (hasMega) {
              const next = new Set(this._mobileExpanded);
              isOpen ? next.delete(mKey) : next.add(mKey);
              this._mobileExpanded = next;
            } else {
              this._selectedTopIndex = idx;
              this._selectedColKey = null;
              this._executeItem(item as unknown as MenuItem, e);
            }
          }}
        >
          ${item.icon
            ? html`<span class="uwc-mm__mob-icon" aria-hidden="true">
                <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
              </span>`
            : nothing}
          <span class="uwc-mm__mob-label">${item.label ?? ''}</span>
          ${item.badge ? html`<span class="uwc-mm__mob-badge">${item.badge}</span>` : nothing}
          ${hasMega
            ? html`<uwc-icon
                name=${isOpen ? 'chevron-up' : 'chevron-down'}
                size="0.85em"
                class="uwc-mm__chevron"
                aria-hidden="true"
              ></uwc-icon>`
            : nothing}
        </button>

        ${hasMega && isOpen ? html`
          <div class="uwc-mm__mob-sub">
            ${(item.items ?? []).map(col => html`
              ${col.label ? html`<p class="uwc-mm__mob-col-header">${col.label}</p>` : nothing}
              <ul class="uwc-mm__mob-col-list">
                ${col.items
                  .filter(it => it.visible !== false)
                  .map(it => it.separator
                    ? html`<li class="uwc-mm__separator" role="separator" aria-hidden="true"></li>`
                    : html`
                      <li role="none">
                        ${it.url && !it.disabled
                          ? html`
                            <a
                              href=${it.url}
                              target=${it.target ?? '_self'}
                              class="uwc-mm__mob-col-item"
                              @click=${(e: MouseEvent) => { e.stopPropagation(); this._selectedTopIndex = idx; this._selectedColKey = null; this._executeItem(it, e); }}
                            >
                              ${it.icon ? html`<span class="uwc-mm__mob-icon" aria-hidden="true"><uwc-icon name=${it.icon} size="1em" library=${it.iconLibrary ?? 'default'}></uwc-icon></span>` : nothing}
                              <span class="uwc-mm__mob-label">${it.label ?? ''}</span>
                              ${it.badge ? html`<span class="uwc-mm__mob-badge">${it.badge}</span>` : nothing}
                            </a>`
                          : html`
                            <button
                              type="button"
                              class="uwc-mm__mob-col-item"
                              ?disabled=${it.disabled}
                              @click=${(e: MouseEvent) => { e.stopPropagation(); this._selectedTopIndex = idx; this._selectedColKey = null; this._executeItem(it, e); }}
                            >
                              ${it.icon ? html`<span class="uwc-mm__mob-icon" aria-hidden="true"><uwc-icon name=${it.icon} size="1em" library=${it.iconLibrary ?? 'default'}></uwc-icon></span>` : nothing}
                              <span class="uwc-mm__mob-label">${it.label ?? ''}</span>
                              ${it.badge ? html`<span class="uwc-mm__mob-badge">${it.badge}</span>` : nothing}
                            </button>`}
                      </li>`)}
              </ul>`)}
          </div>` : nothing}
      </li>`;
  }

  /* ── Root render ─────────────────────────────────────────────────── */

  protected override render(): TemplateResult {
    /* ── Vertical sidebar ── */
    if (this.orientation === 'vertical') {
      return html`
        <nav class="uwc-mm uwc-mm--vertical" aria-label=${this.ariaLabel}>
          <ul class="uwc-mm__v-list" role="tree" aria-label=${this.ariaLabel}>
            ${repeat(
              this._visibleItems(),
              it => it.id ?? it.label ?? Math.random(),
              (it, idx) => this._renderTopItemV(it, idx),
            )}
          </ul>
        </nav>`;
    }

    /* ── Horizontal bar ── */
    return html`
      <nav
        class="uwc-mm"
        aria-label=${this.ariaLabel}
        @click=${(e: MouseEvent) => e.stopPropagation()}
      >
        <!-- Leading slot (logo / brand) -->
        <div class="uwc-mm__start"><slot name="start"></slot></div>

        <!-- Desktop nav list -->
        <ul class="uwc-mm__list" role="menubar" aria-label=${this.ariaLabel}>
          ${repeat(
            this._visibleItems(),
            it => it.id ?? it.label ?? Math.random(),
            (it, idx) => this._renderTopItemH(it, idx),
          )}
        </ul>

        <!-- Trailing slot (search, avatar, etc.) -->
        <div class="uwc-mm__end"><slot name="end"></slot></div>

        <!-- Mobile hamburger -->
        <button
          type="button"
          class="uwc-mm__hamburger"
          aria-label=${this._mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded=${String(this._mobileOpen)}
          aria-controls="uwc-mm-mobile"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            this._mobileOpen = !this._mobileOpen;
            if (this._mobileOpen) { this._activeIndex = null; this._mobileExpanded = new Set(); }
          }}
        >
          <uwc-icon name=${this._mobileOpen ? 'x-lg' : 'list'} size="1.4em" aria-hidden="true"></uwc-icon>
        </button>
      </nav>

      <!-- Mobile accordion panel -->
      <ul
        id="uwc-mm-mobile"
        class="uwc-mm__mobile-panel"
        popover="manual"
        role="menu"
        aria-label=${this.ariaLabel}
        list-style="none"
      >
        ${this._visibleItems().map((it, idx) => this._renderMobileItem(it, idx))}
      </ul>`;
  }
}
